/**
 * Shared chrome for every page under /admin/*. Renders:
 *   - top header (back link + title + optional right-side action)
 *   - the cross-page tab bar (Overview / Users / Waitlist / Programs /
 *     Recipes / Master List) with the active tab highlighted
 *   - children below the tabs
 *
 * Overview / Users / Waitlist all live under /admin (sub-tabs managed by
 * AdminAnalytics via ?tab=…); Programs / Recipes / Master List are their
 * own routes. The tab bar is consistent across all of them.
 */

import { Link } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ADMIN_TABS = [
  { id: "overview",    label: "Overview",    to: "/admin" },
  { id: "users",       label: "Users",       to: "/admin?tab=users" },
  { id: "waitlist",    label: "Waitlist",    to: "/admin?tab=waitlist" },
  { id: "extension-config", label: "Extension Config", to: "/admin/extension-config" },
  { id: "net-trace",         label: "Sync Trace",        to: "/admin/net-trace" },
  { id: "master-list",      label: "Master List",      to: "/admin/master-list" },
  { id: "refdata",          label: "Reference Data",   to: "/admin/refdata" },
  { id: "deals",            label: "Deals",            to: "/admin/deals" },
  { id: "deals-users",      label: "Deals Users",      to: "/admin/deals-users" },
  { id: "partners",         label: "Referral Partners", to: "/admin/partners" },
  { id: "billing",          label: "Billing",          to: "/admin/billing" },
  { id: "llm-usage",         label: "LLM Usage",         to: "/admin/llm-usage" },
  { id: "settings",         label: "Settings",         to: "/admin/settings" },
];

// deals_user role only sees Deals — they have no access to anything else.
const DEALS_ONLY_TABS = new Set(["deals"]);
// partner_admin role sees Deals + their own partner dashboard. The
// partner dashboard lives at /partner (not under /admin); see the
// partner-only tab strip rendered alongside this one in PartnerDashboard.
const PARTNER_ADMIN_TABS = new Set(["deals"]);

export default function AdminLayout({
  activeTab,
  title = "Admin",
  right = null,
  children,
}) {
  const { user, logout } = useAuth();
  const dealsOnly = user && !user.is_admin && user.role === "deals_user";
  const partnerOnly = user && !user.is_admin && user.role === "partner_admin";
  let tabs = ADMIN_TABS;
  if (dealsOnly) tabs = ADMIN_TABS.filter((t) => DEALS_ONLY_TABS.has(t.id));
  else if (partnerOnly) tabs = ADMIN_TABS.filter((t) => PARTNER_ADMIN_TABS.has(t.id));
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="border-b border-black/5 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          {dealsOnly ? (
            <button
              type="button"
              onClick={logout}
              className="flex items-center gap-2 text-[13px] text-neutral-600 hover:text-black"
              data-testid="admin-signout"
            >
              <LogOut size={14} /> Sign out
            </button>
          ) : partnerOnly ? (
            <Link
              to="/dashboard"
              className="flex items-center gap-1.5 text-[13px] text-neutral-600 hover:text-black"
              data-testid="admin-back-link"
            >
              <ArrowLeft size={14} /> Back to dashboard
            </Link>
          ) : (
            <Link to="/dashboard" className="flex items-center gap-2 text-[13px] text-neutral-600 hover:text-black" data-testid="admin-back-link">
              <ArrowLeft size={14} /> Back to dashboard
            </Link>
          )}
          <div className="font-display font-bold tracking-tight">
            Reservoir <span className="text-neutral-400 font-medium">AI</span> · {title}
          </div>
          <div className="flex items-center gap-2 min-w-[120px] justify-end">
            {right}
            {partnerOnly && (
              <button
                type="button"
                onClick={logout}
                className="flex items-center gap-1.5 text-[13px] text-neutral-600 hover:text-black"
                data-testid="admin-signout"
              >
                <LogOut size={14} /> Sign out
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 pt-8 pb-2">
        <div className="flex gap-1.5 border-b border-black/5" data-testid="admin-tabs">
          {tabs.map((t) => {
            const isActive = t.id === activeTab;
            return (
              <Link
                key={t.id}
                to={t.to}
                className={`px-3 py-2 text-[13px] font-semibold border-b-2 -mb-[1px] transition-colors ${
                  isActive ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                {t.label}
              </Link>
            );
          })}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-6">{children}</div>
    </div>
  );
}
